/* eslint-disable vue/one-component-per-file */
import { defineComponent, PropType, Ref, isRef } from 'vue';

import {
  ICellRendererParams,
  IHeaderParams,
  ColDef,
  GridApi,
  RowNode
} from 'ag-grid-community';

import { PlainObject } from './types';
import { transformToFunction } from './Transform';
import { showError } from './Common';
import { TipError } from './Errors';
import CNumberEdit from '../common/widget/CNumberEdit.vue';
import CTextEdit from '../common/widget/CTextEdit.vue';
import CImageView from '../common/widget/CImageView.vue';
import CRemoteSelect from '../common/widget/CRemoteSelect.vue';
import CSelect from '../common/widget/CSelect.vue';
import CObjSel from '../common/widget/CObjSel.vue';
import AgHeaderComponent from '../common/tab/AgHeaderComponent.vue';

import { TableCmdProvider, ITableCmdHandles } from './TableUtilInternal';
import { fixVNodeClickEvent } from './CommonInternal';
import { SummaryFunc } from '../common/tab/SummaryRow.tsx';

import _ from 'lodash-es';

export type { ITableCmdHandles } from './TableUtilInternal';

type CellParams<R, C> = ICellRendererParams<R, C>;

// 组合字段
type ComplexColumnDef<CF extends string> = {
  // 组合字段
  complex?: (
    elements: Record<CF, JSX.Element | JSX.Element[]>
  ) => JSX.Element[];

  // 字段是否visible
  complexVisibles?: () => Readonly<Record<CF, boolean>>;
};

// 组合字段 组件参数
export type ComplexCellParams<R, C, CF extends string> = CellParams<R, C> &
  ComplexColumnDef<CF>;

// 回调函数
type JsxCallback<R, C, CF extends string> = (
  params: ComplexCellParams<R, C, CF>
) => () => JSX.Element;

// jsx 组件
function JsxCell<R, C, CF extends string>(f: JsxCallback<R, C, CF>) {
  return defineComponent({
    props: {
      params: {
        type: Object as PropType<CellParams<R, C> & ComplexColumnDef<CF>>,
        required: true
      }
    },

    setup(props) {
      let vnode: () => JSX.Element;
      try {
        vnode = f(props.params);
      } catch (e: any) {
        console.error('CellRenderer Error: ', e);
        return () => [];
      }

      return () => {
        const node = vnode();
        // 处理onClick
        fixVNodeClickEvent(node);
        return node;
      };
    }
  });
}

type FunCallback<R, C> = (params: CellParams<R, C>) => unknown;

////////////////////////////
// 以下为简化使用函数

// 简化单元格jsx回调
const JsxRenderer = <R, C extends keyof R, CF extends string>(
  f: JsxCallback<R, R[C], CF>
) => JsxCell(f);

const cellEditorMap = {
  // 1. 文本编辑框
  textEdit: CTextEdit,
  // 2. 数字编辑框
  numberEdit: CNumberEdit,
  // 3. 图片
  image: CImageView,
  // 4. 通过远程接口显示下拉, 比如选择货位
  remoteSelect: CRemoteSelect,
  // 5. 一般下拉选择
  select: CSelect,
  // 6. 对象选择
  objSel: CObjSel
};

function isUpperCase(s?: string) {
  if (!s) return false;
  return s.toUpperCase() == s;
}

/**
 * 列信息注解
 * @param headerName 列名
 * @param options
 */
export function ColInfo(
  headerName: string | Ref<string>,
  options: ColDef & {
    convert?: FunCallback<any, any>;
    transform?: string;
    textAlign?: string;
    headerTextAlignRight?: boolean; // 表头文本剧右
    colGroup?: string; // 分组表头名
    colGroupClass?: string; // 分组表头css类
    forceHide?: Ref<boolean>; // 强制隐藏列

    help?: string; // 表头帮助
    redstar?: boolean; // 表头显示红星*
    headerCustomClass?: string; // 表头自定义类

    mode?:
      | 'textEdit'
      | 'numberEdit'
      | 'image'
      | 'remoteSelect'
      | 'select'
      | 'objSel'; // 编辑框方式

    summary?: SummaryFunc; // 汇总行

    editValueChange?: (
      params: ICellRendererParams,
      newValue: any,
      oldValue: any
    ) => void; // 编辑框内容变化
  } = {}
) {
  // 复制, 因为后面有修改
  options = _.clone(options);

  // 表头文字
  if (isRef(headerName)) {
    options.headerName = (() => headerName) as any;
  } else {
    options.headerName = headerName;
  }

  // 变换
  if (options.convert) {
    options.cellRenderer = options.convert;
    delete options.convert;
  } else if (options.transform) {
    options.cellRenderer = transformToFunction(options.transform);
    delete options.transform;
  }

  let cellStyle = options.cellStyle;
  // 文本居左、居右
  if (options.textAlign) {
    cellStyle = {
      ...cellStyle,
      textAlign: options.textAlign
    };
    delete options.textAlign;
  }

  if (options.editValueChange) {
    options.cellRendererParams = {
      ...options.cellRendererParams,
      valueChange: options.editValueChange
    };
    delete options.editValueChange;
  }

  // 编辑框方式
  if (options.mode) {
    cellStyle = {
      ...cellStyle,
      padding: '0px',
      borderColor: 'transparent #eeeeee transparent transparent'
    };

    options.cellRenderer = cellEditorMap[options.mode];
    delete options.mode;

    if (options.sortable == undefined) {
      options.sortable = false;
    }
  }

  if (!options.cellRenderer) {
    // 空的时候显示点位符-
    options.cellRenderer = (params: any) => {
      const value = params.value;
      if (value === '') {
        return '-';
      }
      return value;
    };
  }

  options.cellStyle = cellStyle;

  // 强制隐藏表
  if (options.forceHide) {
    const forceHide = options.forceHide;
    // 转在function, 否则Ref会丢失(可能是deepClone的问题)
    options.forceHide = (() => forceHide) as any;
  }

  // 表头组件
  const headerComponentParams: PlainObject = {};

  // help
  if (options.help) {
    headerComponentParams.helpText = options.help;
    delete options.help;
  }

  // 红星(必要的输入字段)
  if (options.redstar) {
    headerComponentParams.redstar = options.redstar;
    delete options.redstar;
  }

  // 表头自定义class
  if (options.headerCustomClass) {
    headerComponentParams.customClass = options.headerCustomClass;
    delete options.headerCustomClass;
  }

  // 表头文本居右
  if (options.headerTextAlignRight) {
    headerComponentParams.textAlignRight = options.headerTextAlignRight;
    delete options.headerTextAlignRight;
  }

  if (Object.keys(headerComponentParams).length) {
    // 使用自定义header组件
    options.headerComponent = AgHeaderComponent;
    options.headerComponentParams = headerComponentParams;
  }

  return function (target: any, propertyName: string) {
    // 虚拟字段(如vOperate), 默认不排序
    if (
      options.sortable == undefined &&
      propertyName &&
      propertyName.length > 2 &&
      propertyName.charAt(0) == 'v' &&
      isUpperCase(propertyName.charAt(1))
    ) {
      options.sortable = false;
    }

    /*if (options.tooltip) {
      options.tooltipField = propertyName;
      delete options.tooltip;
    }*/

    !target.$fields && (target.$fields = []);
    target.$fields.push({
      ...options,
      field: propertyName
    });
  };
}

/**
 * rowid注解
 */
export function RowId() {
  return function (target: any, propertyName: string) {
    target.$rowid = propertyName;
  };
}

/**
 * 标明表格输入参数
 */
export function FormParam() {
  return function (target: any, propertyName: string) {
    !target.$inputs && (target.$inputs = []);
    target.$inputs.push(propertyName);
  };
}

////////////////////////////////////////////////////////////////////////
type JsxDependencies<R, C> = (params: CellParams<R, C>) => any;

interface ColumnRender<M extends D, F extends keyof M, D = M> {
  jsx: <CF extends string>(
    cb: JsxCallback<D, M[F], CF>,
    options?:
      | {
          deps?: JsxDependencies<M, M[F]>; // // 依赖字段, 控制ag-grid缓存, 相关字段值没变ag-grid不使用缓存
          complexDef?: Record<CF, string | { label: string; show: boolean }>; // 复合字段定义
        }
      | JsxDependencies<any, any>
  ) => void;
}

function jsxRenderFunction(model: any, field: string, virtual?: boolean) {
  return {
    jsx: (
      cb: JsxCallback<any, any, any>,
      options?:
        | {
            deps?: JsxDependencies<any, any>; // 依赖字段, 控制ag-grid缓存, 相关字段值没变ag-grid不使用缓存
            complexDef?: Record<
              string,
              string | { label: string; show: boolean }
            >; // 复合字段定义
          }
        | JsxDependencies<any, any>
    ) => {
      const config = _.find(model.$fields, (c) => c.field == field);
      if (!config) {
        throw Error('field ' + field.toString() + ' not defined');
      }

      config.cellRenderer = JsxRenderer(cb);

      // 组合字段
      if (!_.isFunction(options) && options?.complexDef) {
        config.complexDef = options?.complexDef;
      }

      const deps = _.isFunction(options) ? options : options?.deps;
      // 依赖项, 用于触发
      if (deps == undefined) {
        if (virtual) {
          config.valueGetter = (params: CellParams<any, any>) =>
            JSON.stringify(params.data, (key, value) => {
              if (key.startsWith('_')) {
                return undefined;
              }
              return value;
            });
        }
      } else {
        config.valueGetter = (params: CellParams<any, any>) =>
          JSON.stringify(deps(params));
      }
    }
  };
}

const env = import.meta.env.VITE_APP_ENV;
// 自定义渲染字段应该以v开头
function checkCustomRenderField(field: string, disableWarning?: boolean) {
  if (
    field.length < 2 ||
    !field.startsWith('v') ||
    !isUpperCase(field.charAt(1))
  ) {
    !disableWarning &&
      env == 'development' &&
      console.warn("custom render field must starts with 'v': ", field);
    return false;
  }
  return true;
}

/**
 * 定义列自定义渲染函数
 * @param clazz 模型类
 * @param field
 * @param disableWarning 禁止检查虚拟列v开头检查
 * @returns
 */
function columnRenderer<M, F extends keyof M = any>(
  clazz: { prototype: M },
  field: F,
  disableWarning?: boolean
): ColumnRender<M, F>;
function columnRenderer<M extends D, D>(
  clazz: {
    prototype: M;
  },
  base: {
    prototype: D;
  }
): <F extends keyof M>(
  field: F,
  disableWarning?: boolean
) => ColumnRender<M, F, D>;
function columnRenderer(clazz: any, field: any, disableWarning?: boolean): any {
  const model = clazz.prototype as any;
  if (!_.isString(field)) {
    return (rfield: string, dw?: boolean) => {
      return jsxRenderFunction(
        model,
        rfield,
        checkCustomRenderField(rfield, dw)
      );
    };
  }

  return jsxRenderFunction(
    model,
    field,
    checkCustomRenderField(field, disableWarning)
  );
}

// 表头插件组件
type JsxHeaderCallback<R> = (params: IHeaderParams<R>) => () => JSX.Element;

function JsxHeaderCell<R>(f: JsxHeaderCallback<R>) {
  return defineComponent({
    props: {
      params: {
        type: Object as PropType<IHeaderParams<R>>,
        required: true
      }
    },

    setup(props) {
      let vnode: () => JSX.Element;
      try {
        vnode = f(props.params);
      } catch (e: any) {
        console.error('CellRenderer Error: ', e);
        return () => [];
      }

      return () => {
        const node = vnode();
        // 处理onClick
        fixVNodeClickEvent(node);
        return node;
      };
    }
  });
}

// 表头插槽
function columnHeaderSlot<M, F extends keyof M>(
  clazz: { prototype: M },
  field: F,
  slotName: 'prefixSlot' | 'suffixSlot' | 'labelSlot' | 'belowSlot'
) {
  const model = clazz.prototype as any;

  return {
    jsx: (cb: JsxHeaderCallback<M>) => {
      const config = _.find(model.$fields, (c) => c.field == field);
      if (!config) {
        throw Error('field ' + field.toString() + ' not defined');
      }

      const component = JsxHeaderCell(cb);

      if (!config.headerComponentParams) {
        config.headerComponent = AgHeaderComponent;
        config.headerComponentParams = {};
      }

      config.headerComponentParams[slotName] = component;
    }
  };
}

// 表头前缀slot
function columnHeaderPrefix<M, F extends keyof M>(
  clazz: { prototype: M },
  field: F
) {
  return columnHeaderSlot(clazz, field, 'prefixSlot');
}

// 表头后缀slot
function columnHeaderSuffix<M, F extends keyof M>(
  clazz: { prototype: M },
  field: F
) {
  return columnHeaderSlot(clazz, field, 'suffixSlot');
}

// 表头文本后slot
function columnHeaderLabel<M, F extends keyof M>(
  clazz: { prototype: M },
  field: F
) {
  return columnHeaderSlot(clazz, field, 'labelSlot');
}

// 表头文本后slot
function columnHeaderBelow<M, F extends keyof M>(
  clazz: { prototype: M },
  field: F
) {
  return columnHeaderSlot(clazz, field, 'belowSlot');
}

export {
  columnRenderer,
  columnHeaderPrefix,
  columnHeaderSuffix,
  columnHeaderLabel,
  columnHeaderBelow
};

/////////////////////////////////////////////////////////////////////////
// 菜单处理函数

// 命令回调函数
type TableCmdHandle<D> = (api: GridApi<D>, selectedDatas: D[]) => void;

//export type TableCmdHandles<D> = Record<string, TableCmdHandle<D>>;

// 命令项 描述
class TableCmdItem<D> {
  handle?: TableCmdHandle<D>;

  // 是否需要选择表格中行记录
  // 'none' 不需要, single 允许选一条, multiple 可以选多行
  private _selectMode: 'none' | 'single' | 'multiple';

  private _noSelectTip: string; // 未选择情况下的提示
  private _multiSelectTip: string; // 先多了的提示, 只要selectMode==single下有效

  constructor(
    selectMode: 'none' | 'single' | 'multiple',
    noSelectTip: string,
    multiSelectTip: string
  ) {
    this._selectMode = selectMode;
    this._noSelectTip = noSelectTip;
    this._multiSelectTip = multiSelectTip;
  }

  get selectMode() {
    return this._selectMode;
  }

  get noSelectTip() {
    return this._noSelectTip;
  }

  get multiSelectTip() {
    return this._multiSelectTip;
  }

  // 不需要选择
  noSelect() {
    this._selectMode = 'none';
    return this;
  }

  // 至少要选一条
  requireSelect(noSelectTip?: string) {
    this._selectMode = 'multiple';

    if (noSelectTip) {
      this._noSelectTip = noSelectTip;
    }

    return this;
  }

  // 只能选一条
  exactSingle(multiSelectTip?: string, noSelectTip?: string) {
    this._selectMode = 'single';

    if (multiSelectTip) {
      this._multiSelectTip = multiSelectTip;
    }

    if (noSelectTip) {
      this._noSelectTip = noSelectTip;
    }

    return this;
  }
}

export class TableCmdHandles<D> implements ITableCmdHandles {
  private _cmds: Record<string, TableCmdItem<D>> = {};

  private _selectMode: 'none' | 'single' | 'multiple';
  private _noSelectTip: string; // 未选择情况下的提示
  private _multiSelectTip: string; // 先多了的提示, 只要selectMode==single下有效

  private _tableCtx?: { api: TableCmdProvider };

  // 没用字段, 用于识别类型
  T: TableCmdHandle<D> = {} as TableCmdHandle<D>;

  //
  public static EMPTY = new TableCmdHandles();

  private _cmdsProxy = new Proxy({} as PlainObject, {
    get: (_target, p: string) => {
      return async (selectedRows?: any[]) => {
        await this.execute(p, selectedRows);
      };
    }
  });

  constructor(
    selectMode: 'none' | 'single' | 'multiple' = 'none',
    noSelectTip = '',
    multiSelectTip = ''
  ) {
    this._selectMode = selectMode;
    this._noSelectTip = noSelectTip;
    this._multiSelectTip = multiSelectTip;
  }

  $(name: string): TableCmdItem<D> {
    let cmd = this._cmds[name];
    if (!cmd) {
      cmd = new TableCmdItem<D>(
        this._selectMode,
        this._noSelectTip,
        this._multiSelectTip
      );
      this._cmds[name] = cmd;
    }

    return cmd;
  }

  // 用于事件绑定
  get $$() {
    return this._cmdsProxy;
  }

  init(tableCtx: { api: TableCmdProvider }) {
    this._tableCtx = tableCtx;
  }

  // 执行命令
  async execute(cmd: string, selectedRows?: any[]) {
    const cmdItem = this._cmds[cmd as string];
    if (!cmdItem || !cmdItem.handle) {
      console.warn(`cmd "${cmd}" is not registered`);
      return;
    }

    const agApi = this._tableCtx!.api();
    const api = agApi as GridApi<any>;

    const rows = selectedRows || api.getSelectedRows();
    if (cmdItem.selectMode == 'single') {
      if (rows.length == 0) {
        showError(
          new TipError(cmdItem.noSelectTip || '请从表格中选择', {
            warning: true
          })
        );
        return;
      }

      if (rows.length > 1) {
        showError(
          new TipError(cmdItem.multiSelectTip || '最多只能选一条', {
            warning: true
          })
        );
        return;
      }
    }

    if (cmdItem.selectMode == 'multiple') {
      if (rows.length == 0) {
        showError(
          new TipError(cmdItem.noSelectTip || '至少要选一条', {
            warning: true
          })
        );
        return;
      }
    }

    try {
      await cmdItem.handle(api!, rows);
    } catch (e: unknown) {
      showError(e);
    }
  }
}

//////////////////////////////////////////////////////
// 从ag-grid搜索满足条件的数据
export function agSearch<D>(api: GridApi<D>, filter: (data: D) => boolean) {
  const result: D[] = [];
  api.forEachNode((node) => {
    if (filter(node.data!)) {
      result.push(node.data!);
    }
  });
  return result;
}

// 从ag-grid搜索满足条件的数据, 只返回第一条
export function agSearchFirst<D>(
  api: GridApi<D>,
  filter: (data: D) => boolean
) {
  return agSearchFirstNode(api, filter)?.data;
}

export function agSearchNodes<D>(
  api: GridApi<D>,
  filter: (data: D) => boolean
) {
  const result: RowNode<D>[] = [];
  api.forEachNode((node) => {
    if (filter(node.data!)) {
      result.push(node);
    }
  });
  return result;
}

export function agSearchFirstNode<D>(
  api: GridApi<D>,
  filter: (data: D) => boolean
) {
  let result: RowNode<D> | undefined;
  api.forEachNode((node) => {
    if (result === undefined && filter(node.data!)) {
      result = node;
    }
  });
  return result;
}

/**
 * 取ag-grid中所有的行记录
 * @param agApi
 */
export function agGetAllRows<T>(agApi?: GridApi<T>) {
  const rows: T[] = [];
  agApi?.forEachNode((node) => rows.push(node.data!));
  return rows;
}

/**
 * 取ag-grid所有行数
 * @param agApi
 */
export function agRowCount<T>(agApi?: GridApi<T>) {
  let count = 0;
  agApi?.forEachNode(() => ++count);
  return count;
}

/**
 * 创建虚拟字段, 用于表格中的可编辑字段
 * @param params
 * @param name
 * @param ext
 */
export function createVirtualParams<M, F extends keyof M & string>(
  params: ICellRendererParams<M>,
  name: F,
  ext: PlainObject = {}
) {
  const vName = '$' + name;

  const vParams = {
    ...ext,
    ...params,
    fieldName: vName
  };

  return new Proxy(vParams, {
    get(_, prop: string, receiver: any) {
      if (prop == 'node') {
        const node = params.node;
        return new Proxy(node, {
          get(_, p: string, r: any) {
            if (p == 'setDataValue') {
              return (colKey: string, newValue: any, eventSource?: string) => {
                if (colKey == vName) {
                  params.data![name] = newValue;
                  params.refreshCell?.();
                  return true;
                }

                return node.setDataValue(colKey, newValue, eventSource);
              };
            }

            return Reflect.get(node, p, r);
          },
          set() {
            throw new Error('virtual Cell can"t modified');
          },
          deleteProperty() {
            throw new Error('virtual Cell can"t deleted');
          }
        });
      } else if (prop == 'value') {
        return params.data![name];
      }
      return Reflect.get(vParams, prop, receiver);
    },

    set() {
      throw new Error('virtual Cell can"t modified');
    },

    deleteProperty() {
      throw new Error('virtual Cell can"t deleted');
    }
  });
}

export function createVirtualParams2<M>(
  params: ICellRendererParams<M>,
  names: string[],
  ext: PlainObject = {}
) {
  const vName = '$' + names.join('.');

  const vParams = {
    ...ext,
    ...params,
    fieldName: vName
  };

  return new Proxy(vParams, {
    get(_x, prop: string, receiver: any) {
      if (prop == 'node') {
        const node = params.node;
        return new Proxy(node, {
          get(_y, p: string, r: any) {
            if (p == 'setDataValue') {
              return (colKey: string, newValue: any, eventSource?: string) => {
                if (colKey == vName) {
                  _.set(params.data!, names, newValue);
                  params.refreshCell?.();
                  return true;
                }

                return node.setDataValue(colKey, newValue, eventSource);
              };
            }

            return Reflect.get(node, p, r);
          },
          set() {
            throw new Error('virtual Cell can"t modified');
          },
          deleteProperty() {
            throw new Error('virtual Cell can"t deleted');
          }
        });
      } else if (prop == 'value') {
        return _.get(params.data!, names);
      }
      return Reflect.get(vParams, prop, receiver);
    },

    set() {
      throw new Error('virtual Cell can"t modified');
    },

    deleteProperty() {
      throw new Error('virtual Cell can"t deleted');
    }
  });
}
